import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"
import RequestHelper from "../helpers/request_helper"

// Paired with the shared `datatable` controller, which owns #teams-datatable from its own mount
// on `.panel-body`. This one owns the per-row enable switch, the cross-row validation that runs
// before every submit, and the POST that saves every row at once and reloads the table. It mounts
// on the outer `.panel` because the "Save & Apply" buttons live in `.panel-heading`/`.actions`,
// siblings of `.panel-body`; both controllers' `table` targets sit on the same <table> node, which
// Stimulus namespaces per identifier.
//
// DataTables fires "draw.dt" through jQuery's event bus, not a native DOM event, so a data-action
// never sees it. select2 needs rebinding on each redraw since the row markup is replaced wholesale.
//
// validateTransferData's "Please select a transfer frequency" branch is unreachable: the column is
// NOT NULL with a "monthly" default and the view never renders a blank option.
export default class extends Controller {
  static get targets() {
    return ["table"];
  }

  static get values() {
    return { submitUrl: String };
  }

  connect() {
    this.onDraw = this.onDraw.bind(this);
    $(this.tableTarget).on("draw.dt.creditTransferAutomation", this.onDraw);
    // The initial ajax-driven draw can land before or after this connect(), depending on which of
    // the two sibling controllers wins the mount race, so bind once here too in case it already did.
    this.bindSelects();
  }

  disconnect() {
    $(this.tableTarget).off(".creditTransferAutomation");

    const $initialized = this.selects().filter(".select2-hidden-accessible");
    if ($initialized.length) {
      $initialized.select2("destroy");
    }
  }

  onDraw() {
    this.bindSelects();
  }

  selects() {
    return $(this.tableTarget).find("select");
  }

  bindSelects() {
    this.selects().select2({
      minimumResultsForSearch: Infinity,
      width: "171px"
    });
  }

  enableCreditsTransfer(event) {
    const rowContainer = event.target.closest(".automated-credit-transfer-team-row");
    const disabled = !event.target.checked;

    this.eachIn(rowContainer, ".form-group", (el) => el.classList.toggle("disabled", disabled));
    this.eachIn(rowContainer, ".form-group input", (el) => { el.disabled = disabled; });
    this.eachIn(rowContainer, "select", (el) => { el.disabled = disabled; });
  }

  // Scoped native lookup in place of jQuery's `.find()` -- querySelectorAll's empty-NodeList-on-
  // no-match is the same no-op jQuery gave, so this keeps the all-matches, no-op-on-absent
  // semantics the jQuery .find(selector).<op>() calls it replaces relied on.
  eachIn(root, selector, callback) {
    root.querySelectorAll(selector).forEach(callback);
  }

  saveAndApply() {
    const transferData = this.buildAutomatedTransferData();
    const validation = this.validateTransferData(transferData);

    if (!validation.isValid) {
      window.ltAlert({
        message: validation.errorMessage,
        type: "toast-error",
        duration: 3000
      });

      return;
    }

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    });
    options.body = JSON.stringify({ automated_credit_transfers: transferData });

    fetch(this.submitUrlValue, options).
      then((response) => response.json().then((data) => ({ ok: response.ok, data }))).
      then(({ ok, data }) => {
        if (ok) {
          this.onSaveSuccess(data);
        } else {
          this.onSaveFailure(data);
        }
      }).
      catch(this.logFailedCall);
  }

  onSaveSuccess(data) {
    const teamCount = data.updated_teams_count || 0;
    const message = teamCount > 0 ?
      `Automated transfer settings updated successfully for ${teamCount} team${teamCount > 1 ? "s" : ""}.` :
      "Automated transfer settings saved successfully.";

    window.ltAlert({
      message: message,
      type: "toast-success",
      duration: 2000
    });

    ProgressBar.start();
    $(this.tableTarget).DataTable().ajax.reload(() => ProgressBar.stop());
  }

  onSaveFailure(data) {
    let errorMessage = "Failed to save automated transfer settings.";

    if (data && data.errors && Array.isArray(data.errors)) {
      errorMessage = data.errors.join(", ");
    } else if (data && data.message) {
      errorMessage = data.message;
    }

    window.ltAlert({
      message: errorMessage,
      type: "toast-error",
      duration: 2500
    });
  }

  logFailedCall(response) {
    console.warn(response);
    window.ltAlert({
      message: "Failed to save automated transfer settings.",
      type: "toast-error",
      duration: 2500
    });
  }

  buildAutomatedTransferData() {
    const transferData = [];

    this.tableTarget.querySelectorAll(".automated-credit-transfer-team-row").forEach((row) => {
      const teamId = row.dataset.teamId;

      // Optional chaining on every read here (not just the platform-credits one) matches jQuery's
      // .val()/.is(":checked") on an empty selection returning undefined/false rather than
      // throwing -- act_platform_credits_limit_input_html renders nothing at all unless the org's
      // platform license is enforced, so that one is a real absent-element case, not just defensive.
      const checkbox = row.querySelector('[data-js="act-enabled"]');
      const isEnabled = !!checkbox?.checked;

      const typeSelect = row.querySelector('[data-js="act-type"] select');
      const transferType = typeSelect?.value;

      const ltCreditsInput = row.querySelector('[data-js="act-lt-credits-limit"] input[type="number"]');
      const ltCreditsValue = ltCreditsInput?.value;

      const platformCreditsInput = row.querySelector('[data-js="act-platform-credits-limit"] input[type="number"]');
      const platformCreditsValue = platformCreditsInput?.value;

      let ltCreditsLimit, platformCreditsLimit;

      if (!isEnabled) {
        ltCreditsLimit = 0;
        platformCreditsLimit = 0;
      } else {
        ltCreditsLimit = ltCreditsValue ? parseInt(ltCreditsValue, 10) : null;
        platformCreditsLimit = platformCreditsValue ? parseInt(platformCreditsValue, 10) : null;
      }

      transferData.push({
        id: teamId,
        automated_credits_transfer_enabled: isEnabled,
        automated_credits_transfer_type: transferType,
        automated_lt_credits_transfer_limit: ltCreditsLimit,
        automated_platform_credits_transfer_limit: platformCreditsLimit
      });
    });

    return transferData;
  }

  validateTransferData(transferData) {
    const errors = [];
    const affectedTeams = [];

    transferData.forEach((row) => {
      const teamName = this.tableTarget.querySelector(`[data-team-id="${row.id}"] .details`)?.textContent ||
        `Team ${row.id}`;

      if (row.automated_credits_transfer_enabled) {
        if (!row.automated_credits_transfer_type) {
          affectedTeams.push(teamName);
          errors.push("Please select a transfer frequency");
        }

        const hasLtCredits = row.automated_lt_credits_transfer_limit && row.automated_lt_credits_transfer_limit > 0;
        const hasPlatformCredits = row.automated_platform_credits_transfer_limit && row.automated_platform_credits_transfer_limit > 0;

        if (!hasLtCredits && !hasPlatformCredits) {
          affectedTeams.push(teamName);
          errors.push("Please assign at least one credit limit (LT or Platform)");
        }

        if (row.automated_lt_credits_transfer_limit !== null && row.automated_lt_credits_transfer_limit < 0) {
          affectedTeams.push(teamName);
          errors.push("LT credits limit must be a positive number");
        }

        if (row.automated_platform_credits_transfer_limit !== null && row.automated_platform_credits_transfer_limit < 0) {
          affectedTeams.push(teamName);
          errors.push("Platform credits limit must be a positive number");
        }
      }
    });

    const uniqueErrors = [...new Set(errors)];
    const uniqueAffectedTeams = [...new Set(affectedTeams)];

    return {
      isValid: uniqueErrors.length === 0,
      errorMessage: uniqueErrors.length > 0 ?
        `Errors found for teams: ${uniqueAffectedTeams.join(", ")}. ${uniqueErrors.join(" ")}` : ""
    };
  }
}
